"use client";

import { useEffect, useRef, type MutableRefObject } from "react";
import { isOpenInApp } from "../utils/pwa-utils";

type UseModalCloseOnBrowserButtonOptions = {
  /** When true (default), only trap history while in-app WebView. */
  requireInApp?: boolean;
  /**
   * When set to true before a programmatic close/transition (Skip, Continue, etc.),
   * cleanup replaces the modal history entry instead of calling history.back(),
   * so outbound redirects are not cancelled.
   */
  skipHistoryBackRef?: MutableRefObject<boolean>;
};

const useModalCloseOnBrowserButton = (
  modalOpen: boolean,
  onClose: () => void,
  options?: UseModalCloseOnBrowserButtonOptions
) => {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const requireInApp = options?.requireInApp ?? true;
  const skipHistoryBackRef = options?.skipHistoryBackRef;

  useEffect(() => {
    if ((requireInApp && !isOpenInApp()) || !modalOpen) return;

    document.body.classList.add("modal-open");
    window.history.pushState({ modalOpen: true }, "");

    const handlePopState = () => {
      onCloseRef.current();
    };
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      document.body.classList.remove("modal-open");
      if (window.history.state?.modalOpen) {
        if (skipHistoryBackRef?.current) {
          skipHistoryBackRef.current = false;
          window.history.replaceState(null, "");
        } else {
          window.history.back();
        }
      }
    };
  }, [modalOpen, requireInApp, skipHistoryBackRef]);
};

export default useModalCloseOnBrowserButton;
